import * as THREE from "three";

const VERT = `varying vec3 vP; varying vec2 vW;
void main(){ vP = normalize(position); vW = (modelMatrix * vec4(position, 1.0)).xz; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const SKY = `uniform float uTime; varying vec3 vP;
float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main(){
  float y = vP.y, a = atan(vP.z, vP.x);
  vec3 col = mix(vec3(0.012, 0.02, 0.04), vec3(0.002, 0.004, 0.01), smoothstep(-0.1, 0.7, y));
  col += vec3(0.0, 0.42, 0.55) * 0.16 * exp(-abs(y + 0.02) * 10.0);
  float b1 = sin(a * 3.0 + uTime * 0.05 + sin(y * 6.0 + uTime * 0.1) * 1.5);
  col += vec3(0.32, 0.08, 0.6) * 0.12 * smoothstep(0.6, 1.0, b1) * smoothstep(0.02, 0.35, y) * (1.0 - smoothstep(0.3, 0.9, y));
  float b2 = sin(a * 5.0 - uTime * 0.04 + y * 4.0);
  col += vec3(0.0, 0.55, 0.65) * 0.07 * smoothstep(0.7, 1.0, b2) * smoothstep(0.05, 0.5, y);
  vec2 g = vec2(a * 140.0, y * 140.0); float s = hash(floor(g));
  col += vec3(0.7, 0.9, 1.0) * step(0.9965, s) * (0.5 + 0.5 * sin(uTime * 2.0 + s * 100.0)) * smoothstep(0.0, 0.25, y);
  gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
}`;

const FLOOR = `uniform float uTime; varying vec2 vW;
float line(float v, float w){ float f = abs(fract(v - 0.5) - 0.5) / max(fwidth(v), 1e-4); return 1.0 - min(f / w, 1.0); }
void main(){
  float r = length(vW), fade = exp(-r * 0.05);
  float g1 = max(line(vW.x, 1.0), line(vW.y, 1.0)), g2 = max(line(vW.x / 5.0, 1.4), line(vW.y / 5.0, 1.4));
  float pulse = 1.0 - smoothstep(0.0, 1.6, abs(r - mod(uTime * 5.0, 60.0)));
  vec3 col = vec3(0.0, 0.5, 0.65) * (g1 * 0.18 + g2 * 0.5) * fade;
  col += vec3(0.0, 0.9, 1.0) * pulse * 0.45 * fade * (0.25 + g1);
  col += vec3(0.55, 0.25, 1.0) * line(r / 4.0, 1.2) * 0.22 * fade;
  gl_FragColor = vec4(clamp(col, 0.0, 4.0), 1.0);
}`;

function streakTexture() {
  const c = document.createElement("canvas"); c.width = 512; c.height = 64;
  const g = c.getContext("2d"), grd = g.createRadialGradient(256, 32, 0, 256, 32, 256);
  grd.addColorStop(0, "rgba(255,255,255,1)"); grd.addColorStop(0.08, "rgba(255,255,255,0.55)"); grd.addColorStop(0.4, "rgba(255,255,255,0.12)"); grd.addColorStop(1, "rgba(255,255,255,0)");
  g.fillStyle = grd; g.fillRect(0, 0, 512, 64);
  return new THREE.CanvasTexture(c);
}

export class Holo {
  constructor(scene, lowPower) {
    this.group = new THREE.Group(); this.u = { uTime: { value: 0 } };
    const add = (o) => (this.group.add(o), o);
    add(new THREE.Mesh(new THREE.SphereGeometry(95, 48, 24), new THREE.ShaderMaterial({ side: THREE.BackSide, depthWrite: false, uniforms: this.u, vertexShader: VERT, fragmentShader: SKY })));
    const floor = add(new THREE.Mesh(new THREE.PlaneGeometry(170, 170), new THREE.ShaderMaterial({ uniforms: this.u, vertexShader: VERT, fragmentShader: FLOOR, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false })));
    floor.rotation.x = -Math.PI / 2; floor.position.y = -2.42;
    const add2 = (m) => Object.assign(new THREE.MeshBasicMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }), m);
    this.rings = [[10.5, 0x00f0ff, 0.5, [1.35, 0, 0.1]], [12.2, 0x9b5cff, 0.35, [1.2, 0.3, -0.2]], [14.0, 0xffb800, 0.22, [1.7, -0.2, 0.3]]].map(([R, color, opacity, rot]) => {
      const m = add(new THREE.Mesh(new THREE.TorusGeometry(R, 0.014, 6, 220), add2({ color, opacity })));
      m.rotation.set(...rot); m.position.y = 0.6; return m;
    });
    const ticks = [];
    for (let k = 0; k < 240; k++) {
      const a = (k / 240) * Math.PI * 2, r0 = 8.4, r1 = k % 10 === 0 ? 9.1 : k % 5 === 0 ? 8.8 : 8.6;
      ticks.push(Math.cos(a) * r0, 0, Math.sin(a) * r0, Math.cos(a) * r1, 0, Math.sin(a) * r1);
    }
    const tg = new THREE.BufferGeometry(); tg.setAttribute("position", new THREE.Float32BufferAttribute(ticks, 3));
    this.dial = add(new THREE.LineSegments(tg, new THREE.LineBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.45, blending: THREE.AdditiveBlending })));
    this.dial.position.y = -2.3;
    const pillarMat = add2({ color: 0x00f0ff, opacity: 0.32 }), pg = new THREE.CylinderGeometry(0.025, 0.025, 16, 6);
    [[-6.2, -6.2], [6.2, -6.2], [6.2, 6.2], [-6.2, 6.2]].forEach(([x, z]) => { const p = add(new THREE.Mesh(pg, pillarMat)); p.position.set(x, 5.6, z); });
    const n = lowPower ? 300 : 1400, mp = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { const a = Math.random() * Math.PI * 2, r = 7 + Math.random() * 22; mp[i * 3] = Math.cos(a) * r; mp[i * 3 + 1] = -2 + Math.random() * 14; mp[i * 3 + 2] = Math.sin(a) * r; }
    const mg = new THREE.BufferGeometry(); mg.setAttribute("position", new THREE.BufferAttribute(mp, 3));
    this.motes = add(new THREE.Points(mg, new THREE.PointsMaterial({ color: 0x7ff6ff, size: 0.06, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending, depthWrite: false })));
    const tex = streakTexture();
    this.flares = [[7, 5, 7, 0xffb800, 9], [8, 4, -7, 0x00f0ff, 11], [-6.2, 13.6, -6.2, 0x00f0ff, 6], [6.2, 13.6, 6.2, 0x9b5cff, 6]].map(([x, y, z, color, w]) => {
      const s = add(new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color, transparent: true, opacity: 0.3, blending: THREE.AdditiveBlending, depthWrite: false, fog: false })));
      s.position.set(x, y, z); s.scale.set(w, w * 0.06, 1); s.userData.w = w; return s;
    });
    this.scan = add(new THREE.Mesh(new THREE.PlaneGeometry(10.6, 0.35), add2({ color: 0x00f0ff, opacity: 0.12, side: THREE.DoubleSide })));
    this.scan.rotation.x = -Math.PI / 2;
    scene.add(this.group);
  }
  update(t, top) {
    this.u.uTime.value = t;
    this.rings.forEach((r, i) => (r.rotation.z += 0.0008 * (i % 2 ? -1 : 1) * (i + 1)));
    this.dial.rotation.y = t * 0.02;
    const p = this.motes.geometry.attributes.position;
    for (let i = 0; i < p.count; i++) { let y = p.array[i * 3 + 1] + 0.004; if (y > 12) y = -2; p.array[i * 3 + 1] = y; }
    p.needsUpdate = true;
    this.flares.forEach((f, i) => { const k = 1 + Math.sin(t * 0.7 + i) * 0.12; f.scale.set(f.userData.w * k, f.userData.w * 0.06, 1); });
    this.scan.position.set(0, top + 0.06, ((t * 0.9) % 12) - 6);
    this.scan.visible = Math.abs(this.scan.position.z) < 5.2;
  }
}
